import type { UserAvatar } from '../../api/people';
import { uiText } from '../../i18n';
import { PhotoOrderForm } from '../photo/PhotoOrderForm';
import { CollageOrderForm } from '../collage/CollageOrderForm';
import { VoiceOrderForm } from '../voice/VoiceOrderForm';
import { AvatarOrderForm } from '../avatar/AvatarOrderForm';
import { VideoEntryForm } from '../video/VideoEntryForm';
import { MotionEntryForm } from '../motion/MotionEntryForm';
import { TextEntryForm } from '../text/native/TextEntryForm';
import { useEffect } from 'react';
import { openProjectsWith } from '../script/Projects';
import { CircleOrderForm } from './circle/CircleOrderForm';
import { ShootOrderForm } from '../shoot/ShootOrderForm';
import { PShootOrderForm } from '../pshoot/PShootOrderForm';
import { ReelsOrderForm } from '../reels/ReelsOrderForm';
import { MCardOrderForm } from '../mcard/MCardOrderForm';
import { PostOrderForm } from '../post/PostOrderForm';
import { InfogOrderForm } from '../infog/InfogOrderForm';
import { CoverOrderForm } from '../cover/CoverOrderForm';
import { StudioIcon } from './StudioIcon';
import type { StudioApp } from './studio-api';
import { text } from './studio-content';

/** «Озвучка проектов» живёт в разделе «Проекты»: студия передаёт туда сценарий */
function ToProjects({ text }: { text: string }) {
  useEffect(() => openProjectsWith(text), [text]);
  return null;
}

/** Только продуктовые React-экраны. Схема API не подменяет интерфейс инструмента. */
export function StudioScreen({
  app,
  avatar,
  initialInputs,
  onBack,
  registerBack,
  active = true,
  templateEntry = false,
}: {
  app: StudioApp;
  avatar: UserAvatar | null;
  initialInputs?: Record<string, unknown>;
  onBack(): void;
  registerBack?(handler: () => boolean): () => void;
  active?: boolean;
  /** открыт «Попробовать» листа шаблона (только кружок: формат и название задаёт шаблон) */
  templateEntry?: boolean;
}) {
  const props = { app, avatar, initialInputs };
  switch (app.entry.screen) {
    case 'who':
    case 'circle':
    case 'lipsync':
      return <CircleOrderForm {...props} templateEntry={templateEntry} />;
    case 'photo':
      return <PhotoOrderForm {...props} headerActive={active} />;
    case 'collage':
      return <CollageOrderForm {...props} />;
    case 'shoot':
      return <ShootOrderForm {...props} />;
    case 'pshoot':
      return <PShootOrderForm {...props} />;
    case 'reels':
      return <ReelsOrderForm {...props} headerActive={active} />;
    case 'mcard':
      return <MCardOrderForm {...props} headerActive={active} />;
    case 'post':
      return <PostOrderForm {...props} headerActive={active} />;
    case 'infog':
      return <InfogOrderForm {...props} headerActive={active} />;
    case 'cover':
      return <CoverOrderForm {...props} headerActive={active} />;
    case 'voice':
      return <VoiceOrderForm {...props} />;
    case 'avatar':
      return <AvatarOrderForm {...props} registerBack={registerBack} />;
    case 'video':
      return <VideoEntryForm {...props} headerActive={active} />;
    case 'dance':
    case 'swap':
      return <MotionEntryForm {...props} />;
    case 'text':
      return <TextEntryForm {...props} />;
    case 'script':
      return <ToProjects text={text(initialInputs?.text)} />;
    default:
      return (
        <section className="st-zero" role="status">
          <StudioIcon />
          <h3>{uiText('Этот инструмент пока недоступен')}</h3>
          <p>{uiText('Выберите другой инструмент или вернитесь к шаблонам.')}</p>
          <button type="button" className="btn line" onClick={onBack}>
            {uiText('Вернуться')}
          </button>
        </section>
      );
  }
}
